import { useState, useCallback, useEffect } from "react";

const LOCK_TIMEOUT = 60000; // 60 seconds

/**
 * Hook for exclusive access to a resource across tabs
 * @param frameId Unique identifier for the current frame
 * @param conversationId Current conversation identifier (optional)
 * @returns Object with lock states and withLock function
 */
function useExclusive(frameId: string, conversationId: string | undefined) {
  // NOTE(liamvdv): navigator.locks is not supported on Chrome before 2018;
  //                in http (not https) context
  //                in WebView
  //                browser enterprise flags disabled them
  //                https://caniuse.com/mdn-api_navigator_locks
  if (!("locks" in navigator)) {
    return {
      isLocked: false,
      withLock: async <T>(promise: Promise<T>) => promise,
    };
  }
  const [lockState, setLockState] = useState<
    "unlocked" | "our_lock" | "their_lock"
  >("unlocked");
  // Check lock state periodically only if we have a conversationId
  useEffect(() => {
    if (!conversationId) {
      setLockState("unlocked");
      return;
    }

    const lockName = `frame_${frameId}_conversation_${conversationId}`;
    const checkLockState = async () => {
      const state = await navigator.locks.query();
      const lock = state.held?.find((lock) => lock.name === lockName);
      const isCurrentlyLocked = !!lock;
      setLockState((v) =>
        isCurrentlyLocked
          ? v === "our_lock"
            ? "our_lock"
            : "their_lock"
          : "unlocked"
      );
    };

    const interval = setInterval(checkLockState, 1000);
    checkLockState();

    return () => {
      clearInterval(interval);
    };
  }, [conversationId, frameId]);

  const withLock = useCallback(
    async <T>(promise: Promise<T>): Promise<T> => {
      if (!conversationId) {
        return promise;
      }
      const lockName = `frame_${frameId}_conversation_${conversationId}`;
      const controller = new AbortController();

      const timeoutId = setTimeout(() => controller.abort(), LOCK_TIMEOUT);

      try {
        return await navigator.locks.request(
          lockName,
          { mode: "exclusive", signal: controller.signal },
          async (lock) => {
            setLockState("our_lock");
            return await promise;
          }
        );
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          console.error("Lock acquisition timed out after", LOCK_TIMEOUT, "ms");
        }
        throw error;
      } finally {
        setLockState("unlocked");
        clearTimeout(timeoutId);
      }
    },
    [conversationId, frameId]
  );
  return { isLocked: lockState == "their_lock", withLock };
}

export default useExclusive;
